import React, { useState, useEffect } from 'react';
import { ActivityEvent } from '@/types';
import { formatRelativeTime, cn } from '@/utils';

interface ActivityFeedProps {
  events: ActivityEvent[];
  autoRefresh?: boolean;
}

export const ActivityFeed: React.FC<ActivityFeedProps> = ({ 
  events: initialEvents, 
  autoRefresh = true 
}) => {
  const [events, setEvents] = useState(initialEvents);
  const [isAutoRefresh, setIsAutoRefresh] = useState(autoRefresh);

  useEffect(() => {
    if (!isAutoRefresh) return;

    const interval = setInterval(() => {
      // In a real app, this would fetch new events from an API
      // For now, we'll just update the timestamps
      setEvents(current => [...current]);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [isAutoRefresh]);

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'lead_ingested':
        return '👤';
      case 'email_sent':
        return '📧';
      case 'sms_sent':
        return '📱';
      case 'engagement':
        return '🎯';
      case 'sequence_completed':
        return '✅';
      case 'handoff':
        return '🤝';
      case 'stall_detected':
        return '⚠️';
      default:
        return '📝';
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'engagement':
      case 'sequence_completed':
      case 'handoff':
        return 'text-emerald-400';
      case 'stall_detected':
        return 'text-amber-400';
      case 'lead_ingested':
        return 'text-blue-400';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <div className="bg-slate-700 rounded-lg border border-slate-600 p-6 h-80">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-50">
          Live Activity Feed
        </h2>
        <button
          onClick={() => setIsAutoRefresh(!isAutoRefresh)}
          className={cn(
            'text-sm px-3 py-1 rounded transition-colors',
            isAutoRefresh 
              ? 'bg-emerald-600 text-white' 
              : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
          )}
        >
          🔄 {isAutoRefresh ? 'Auto' : 'Manual'}
        </button>
      </div>

      <div className="space-y-3 overflow-y-auto h-56">
        {events.map((event) => (
          <div key={event.id} className="flex items-start space-x-3 p-2 hover:bg-slate-600/50 rounded">
            <div className="flex-shrink-0 mt-1">
              <span className="text-lg" role="img" aria-hidden="true">
                {getEventIcon(event.type)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <p className={cn('text-sm', getEventColor(event.type))}>
                  {event.description}
                </p>
                <span className="text-xs text-slate-500 flex-shrink-0 ml-2">
                  {formatRelativeTime(event.timestamp)}
                </span>
              </div>
              {event.leadName && (
                <div className="mt-1">
                  <button className="text-xs text-blue-400 hover:text-blue-300">
                    [View Lead]
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-center">
        <button className="text-sm text-slate-400 hover:text-white">
          Show All Activity
        </button>
      </div>
    </div>
  );
};